import React from 'react';
import { Button, Chip, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useTranslation } from 'react-i18next';
import { OrderData } from '../../../api';
import { BODY_WEIGHT, CARD_RADIUS, FONT_SIZE, MUTED_TEXT } from '../agendaStyles';
import { CloseDaySummary } from '../utils/closeDay';
import { formatSeconds } from '../utils/areasFormat';

type CloseDaySectionProps = {
  summary: CloseDaySummary;
  timeFormat: string;
  formatAmount: (amount: number) => React.ReactNode;
  onCollect: (order: OrderData) => void;
  onCloseWithNote: (order: OrderData) => void;
};

const orderName = (order: OrderData) => [order.firstname, order.lastname].filter(Boolean).join(' ');

const TIME_WIDTH = '56px';

const OrderLabel = ({ order, isMuted = false }: { order: OrderData; isMuted?: boolean }) => {
  const { t } = useTranslation();
  return (
    <Typography
      sx={{
        flex: '1 1 120px',
        minWidth: 0,
        fontSize: isMuted ? FONT_SIZE.secondary : FONT_SIZE.body,
        fontWeight: isMuted ? BODY_WEIGHT : 600,
        color: isMuted ? MUTED_TEXT : undefined,
        wordBreak: 'break-word',
      }}>
      {orderName(order) || t('calendar.areas_view.unnamed_order')}
      {order.order_number !== undefined && (
        <Typography
          component="span"
          dir="ltr"
          sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, color: MUTED_TEXT, marginInlineStart: 0.75 }}>
          #{order.order_number}
        </Typography>
      )}
      {isMuted && ` · ${t('calendar.areas_view.closed_with_note_line', { note: order.balance_closed_note })}`}
    </Typography>
  );
};

export const CloseDaySection = ({
  summary,
  timeFormat,
  formatAmount,
  onCollect,
  onCloseWithNote,
}: CloseDaySectionProps) => {
  const { t } = useTranslation();
  const openTotal = summary.open.reduce((total, order) => total + (order.balance_due ?? 0), 0);
  const timeOf = (order: OrderData) => (
    <Typography
      dir="ltr"
      sx={{
        width: TIME_WIDTH,
        flexShrink: 0,
        fontSize: FONT_SIZE.secondary,
        color: MUTED_TEXT,
        fontVariantNumeric: 'tabular-nums',
      }}>
      {formatSeconds(order.time ?? 0, timeFormat)}
    </Typography>
  );

  return (
    <Paper
      component="section"
      variant="outlined"
      sx={{
        borderRadius: CARD_RADIUS,
        borderColor: 'divider',
        backgroundColor: 'mb.surface',
        padding: { xs: 2, md: 2.5 },
        marginBottom: 1.75,
      }}>
      <Grid container direction="column" wrap="nowrap" gap={1.75}>
        <Grid container alignItems="center" wrap="nowrap" gap={1.5}>
          <ReceiptLongOutlinedIcon sx={{ fontSize: '24px', color: 'primary.main' }} />
          <Typography component="h3" sx={{ flex: 1, fontSize: FONT_SIZE.title, fontWeight: 700 }}>
            {t('calendar.areas_view.close_day_title')}
          </Typography>
          {summary.open.length > 0 && (
            <Chip
              size="small"
              label={
                <>
                  {t('calendar.areas_view.close_day_open_count', { count: summary.open.length })}
                  {' · '}
                  <Typography component="span" dir="ltr" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                    {formatAmount(openTotal)}
                  </Typography>
                </>
              }
              sx={{
                color: 'mb.danger',
                backgroundColor: 'mb.dangerSoft',
                fontWeight: 600,
                fontSize: 13,
                borderRadius: '4px',
              }}
            />
          )}
        </Grid>

        <LinearProgress
          variant="determinate"
          value={(summary.closedCount / summary.totalCount) * 100}
          sx={{
            height: 6,
            borderRadius: '4px',
            backgroundColor: 'mb.lineSoft',
            '& .MuiLinearProgress-bar': { borderRadius: '4px', backgroundColor: 'mb.green' },
          }}
        />
        <Typography sx={{ fontSize: FONT_SIZE.secondary, color: MUTED_TEXT, marginTop: -0.75 }}>
          {t('calendar.areas_view.close_day_progress', { closed: summary.closedCount, total: summary.totalCount })}
        </Typography>

        {summary.open.length === 0 && (
          <Grid container direction="column" alignItems="center" gap={1.25} sx={{ paddingBlock: 1 }}>
            <Grid
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'mb.mint',
                color: 'mb.green',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <CheckIcon sx={{ fontSize: 30 }} />
            </Grid>
            <Typography sx={{ fontSize: '17px', fontWeight: 600, textAlign: 'center' }}>
              {t('calendar.areas_view.close_day_done')}
            </Typography>
          </Grid>
        )}

        {summary.open.map((order) => (
          <Grid
            key={order.id}
            container
            alignItems="center"
            gap={1.5}
            rowGap={1}
            sx={{
              paddingTop: 1.25,
              borderTop: '1px solid',
              borderColor: 'mb.lineSoft',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}>
            {timeOf(order)}
            <OrderLabel order={order} />
            <Typography dir="ltr" sx={{ fontWeight: 700, color: 'mb.danger', flexShrink: 0 }}>
              {formatAmount(order.balance_due ?? 0)}
            </Typography>
            <Grid
              container
              wrap="nowrap"
              gap={1}
              sx={{
                width: { xs: '100%', md: 'auto' },
                flexShrink: 0,
                flexDirection: { xs: 'row-reverse', md: 'row' },
              }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => onCloseWithNote(order)}
                sx={{ minHeight: { xs: 44, md: 40 }, flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}>
                {t('calendar.areas_view.close_with_note')}
              </Button>
              <Button
                variant="contained"
                onClick={() => onCollect(order)}
                sx={{ minHeight: { xs: 44, md: 40 }, flex: { xs: 1, md: 'none' }, boxShadow: 'none' }}>
                {t('calendar.areas_view.collect')}
              </Button>
            </Grid>
          </Grid>
        ))}

        {summary.closedWithNote.map((order) => (
          <Grid
            key={order.id}
            container
            alignItems="center"
            gap={1.5}
            wrap="nowrap"
            sx={{ paddingTop: 1.25, borderTop: '1px solid', borderColor: 'mb.lineSoft' }}>
            {timeOf(order)}
            <OrderLabel order={order} isMuted />
            <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.secondary, color: MUTED_TEXT, flexShrink: 0 }}>
              {formatAmount(Math.max(0, order.balance_due ?? 0))}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
